import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { SunsetAPIFactionContext } from "../../Networking/Interface/Faction/FactionApiProvider";
import { Faction, FactionType } from "../../Networking/Interface/Faction/FactionInterface";
import { War } from "../../Networking/Interface/Faction/WarInterface";
import { InitialsAPI } from "../../Networking/InitialsAPI";
import { FactionColours, getBannerType } from "./FactionUtils";
import { UserContext } from "../../User/UserContext";
import { useToast } from "../../Components/Toast/ToastContext";
import { ToastType } from "../../Components/Toast/Toast";
import styles from "./FactionsDashboardNew.module.css";

// ---- helpers ----------------------------------------------------------------

const getFactionColor = (faction: Faction): string =>
  FactionColours[faction.color] || "#FFFFFF";

const typeLabel = (t: FactionType) => {
  switch (t) {
    case FactionType.goverment:
      return "Government";
    case FactionType.business:
      return "Business";
    case FactionType.criminal:
      return "Criminal";
    default:
      return "Unknown";
  }
};

const typeBadgeClass = (t: FactionType) => {
  switch (t) {
    case FactionType.goverment:
      return styles.typeBadgeGov;
    case FactionType.business:
      return styles.typeBadgeBiz;
    case FactionType.criminal:
      return styles.typeBadgeCrim;
    default:
      return styles.typeBadgeBiz;
  }
};

const typeBgColor = (t: FactionType) => {
  switch (t) {
    case FactionType.goverment:
      return "rgba(52,152,219,0.85)";
    case FactionType.business:
      return "rgba(231,117,46,0.85)";
    case FactionType.criminal:
      return "rgba(231,76,60,0.85)";
    default:
      return "#888";
  }
};

type FilterType = "all" | "government" | "business" | "criminal";
type SortField = "name" | "points" | "level" | "created";
type ViewMode = "grid" | "list";
type TabKey = "browse" | "myFactions" | "posts";

// ---- component --------------------------------------------------------------

export const FactionsDashboardNew: React.FC = () => {
  const api = useContext(SunsetAPIFactionContext);
  const userProvider = useContext(UserContext);
  const { showToast } = useToast();
  const navigate = useNavigate();

  // data
  const [factions, setFactions] = useState<Faction[]>([]);
  const [activeWars, setActiveWars] = useState<War[]>([]);
  const [loading, setLoading] = useState(true);

  // UI state
  const [filter, setFilter] = useState<FilterType>("all");
  const [sortField, setSortField] = useState<SortField>("name");
  const [search, setSearch] = useState("");
  const [viewMode, setViewMode] = useState<ViewMode>("grid");
  const [activeTab, setActiveTab] = useState<TabKey>("browse");

  // ---- fetch data -----------------------------------------------------------
  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      try {
        const [facRes, warRes] = await Promise.all([
          api!.getFactions(),
          api!.getActiveWars(),
        ]);
        if (!mounted) return;
        if (facRes.success) setFactions(facRes.data ?? []);
        if (warRes.success) setActiveWars(warRes.data ?? []);
        if ((facRes as any).code === 429 || (warRes as any).code === 429) {
          showToast(ToastType.Warning, "Rate-limited – try again shortly.", 4000);
        }
      } catch {
        if (mounted) showToast(ToastType.Error, "Failed to load factions.", 4000);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api]);

  // ---- derived data ---------------------------------------------------------
  const activeWarFactionIds = useMemo(() => {
    const ids = new Set<number>();
    activeWars.forEach((w) =>
      w.participants?.forEach((p) => ids.add(p.faction_id))
    );
    return ids;
  }, [activeWars]);

  const filteredFactions = useMemo(() => {
    let list = [...factions];

    // type filter
    if (filter === "government") list = list.filter((f) => f.type === FactionType.goverment);
    else if (filter === "business") list = list.filter((f) => f.type === FactionType.business);
    else if (filter === "criminal") list = list.filter((f) => f.type === FactionType.criminal);

    // search
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (f) =>
          f.name.toLowerCase().includes(q) ||
          f.tag.toLowerCase().includes(q) ||
          (f.slogan ?? "").toLowerCase().includes(q)
      );
    }

    // sort
    list.sort((a, b) => {
      switch (sortField) {
        case "points":
          return (b.points ?? 0) - (a.points ?? 0);
        case "level":
          return (b.level ?? 0) - (a.level ?? 0);
        case "created":
          return new Date(b.created).getTime() - new Date(a.created).getTime();
        default:
          return a.name.localeCompare(b.name);
      }
    });

    return list;
  }, [factions, filter, search, sortField]);

  const myFactions = useMemo(() => {
    if (!userProvider.user?.factions) return [];
    const tags = userProvider.user.factions.map((f: any) => f.factionTag);
    return factions.filter((f) => tags.includes(f.tag));
  }, [userProvider.user, factions]);

  // ---- aggregate stats ------------------------------------------------------
  const govCount = factions.filter((f) => f.type === FactionType.goverment).length;
  const bizCount = factions.filter((f) => f.type === FactionType.business).length;
  const crimCount = factions.filter((f) => f.type === FactionType.criminal).length;

  // ---- render ---------------------------------------------------------------
  if (loading) return <div className={styles.loading}>Loading factions…</div>;

  return (
    <div className={styles.wrapper}>
      {/* page header */}
      <h1 className={styles.pageTitle}>Factions</h1>
      <p className={styles.pageSubtitle}>
        Browse all factions or view your own. See who's at war and check out the latest faction posts.
      </p>

      {/* stats banner */}
      <div className={styles.statsBanner}>
        <div className={styles.statCard}>
          <div className={styles.statValue}>{factions.length}</div>
          <div className={styles.statLabel}>Total Factions</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statValue}>{govCount}</div>
          <div className={styles.statLabel}>Government</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statValue}>{bizCount}</div>
          <div className={styles.statLabel}>Business</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statValue}>{crimCount}</div>
          <div className={styles.statLabel}>Criminal</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statValue}>{activeWars.length}</div>
          <div className={styles.statLabel}>Active Wars</div>
        </div>
      </div>

      {/* active wars spotlight */}
      {activeWars.length > 0 && (
        <section className={styles.activeWarsSection}>
          <h2 className={styles.sectionTitle}>Active Wars</h2>
          <div className={styles.activeWarsRow}>
            {activeWars.slice(0, 4).map((war) => {
              const teams = war.participants ?? [];
              const team1Ids = teams.filter((p) => p.team === 1).map((p) => p.faction_id);
              const team2Ids = teams.filter((p) => p.team === 2).map((p) => p.faction_id);
              const nameOf = (id: number) => factions.find((f) => f.id === id)?.name ?? `#${id}`;
              const started = new Date(war.start_date).toLocaleDateString();
              return (
                <Link key={war.id} to="/wars" className={styles.activeWarCard}>
                  <div>
                    <div className={styles.warTeamName}>
                      {team1Ids.map(nameOf).join(", ") || "Team 1"}
                    </div>
                    <div className={styles.warVs}>vs</div>
                    <div className={styles.warTeamName}>
                      {team2Ids.map(nameOf).join(", ") || "Team 2"}
                    </div>
                  </div>
                  <div className={styles.warDuration}>Since {started}</div>
                </Link>
              );
            })}
          </div>
        </section>
      )}

      {/* tabs */}
      <div className={styles.tabs}>
        {(["browse", "myFactions", "posts"] as TabKey[]).map((key) => (
          <button
            key={key}
            className={`${styles.tab} ${activeTab === key ? styles.tabActive : ""}`}
            onClick={() => setActiveTab(key)}
          >
            {key === "browse" ? "Browse Factions" : key === "myFactions" ? "My Factions" : "Faction Posts"}
          </button>
        ))}
      </div>

      {/* ---- Browse tab ---- */}
      {activeTab === "browse" && (
        <>
          {/* filter & sort bar */}
          <div className={styles.filterBar}>
            <div className={styles.filterPills}>
              {(
                [
                  ["all", "All"],
                  ["government", "Government"],
                  ["business", "Business"],
                  ["criminal", "Criminal"],
                ] as [FilterType, string][]
              ).map(([key, label]) => (
                <button
                  key={key}
                  className={`${styles.filterPill} ${filter === key ? styles.filterPillActive : ""}`}
                  onClick={() => setFilter(key)}
                >
                  {label}
                </button>
              ))}
            </div>

            <input
              className={styles.searchInput}
              placeholder="Search by name, tag or slogan…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />

            <select
              className={styles.sortSelect}
              value={sortField}
              onChange={(e) => setSortField(e.target.value as SortField)}
            >
              <option value="name">Sort: Name</option>
              <option value="points">Sort: Points</option>
              <option value="level">Sort: Level</option>
              <option value="created">Sort: Newest</option>
            </select>

            <div className={styles.viewToggle}>
              <button
                className={`${styles.viewBtn} ${viewMode === "grid" ? styles.viewBtnActive : ""}`}
                onClick={() => setViewMode("grid")}
                title="Grid view"
              >
                ▦
              </button>
              <button
                className={`${styles.viewBtn} ${viewMode === "list" ? styles.viewBtnActive : ""}`}
                onClick={() => setViewMode("list")}
                title="List view"
              >
                ☰
              </button>
            </div>
          </div>

          {filteredFactions.length === 0 ? (
            <p className={styles.emptyState}>No factions match your filters.</p>
          ) : viewMode === "grid" ? (
            <div className={styles.factionGrid}>
              {filteredFactions.map((faction) => (
                <FactionCard
                  key={faction.id}
                  faction={faction}
                  atWar={activeWarFactionIds.has(faction.id)}
                />
              ))}
            </div>
          ) : (
            <FactionTableView
              factions={filteredFactions}
              activeWarFactionIds={activeWarFactionIds}
              onRowClick={(tag) => navigate(`/faction/${tag}`)}
            />
          )}
        </>
      )}

      {/* ---- My Factions tab ---- */}
      {activeTab === "myFactions" && (
        <section className={styles.myFactionsSection}>
          {myFactions.length === 0 ? (
            <p className={styles.emptyState}>You are not in any factions.</p>
          ) : (
            <div className={styles.myFactionsGrid}>
              {myFactions.map((faction) => (
                <FactionCard
                  key={faction.id}
                  faction={faction}
                  atWar={activeWarFactionIds.has(faction.id)}
                />
              ))}
            </div>
          )}
        </section>
      )}

      {/* ---- Faction Posts tab ---- */}
      {activeTab === "posts" && (
        <section className={styles.postsSection}>
          <div className={styles.disclaimer}>
            This feature is not implemented as of right now.
          </div>
        </section>
      )}
    </div>
  );
};

// ---- FactionCard (grid) -----------------------------------------------------

interface FactionCardProps {
  faction: Faction;
  atWar: boolean;
}

const FactionCard: React.FC<FactionCardProps> = ({ faction, atWar }) => {
  const bannerSrc =
    faction.bannerURL ||
    `${process.env.PUBLIC_URL}/DefaultFactionBanners/${getBannerType(faction.type)}.png`;
  const avatarSrc =
    faction.profileURL ||
    new InitialsAPI().getAvatarURL({ name: faction.tag, length: 4, fontSize: 0.35 });

  return (
    <Link className={styles.factionCard} to={`/faction/${faction.tag}`}>
      {/* type badge */}
      <span className={`${styles.typeBadge} ${typeBadgeClass(faction.type)}`}>
        {typeLabel(faction.type)}
      </span>

      {/* active war badge */}
      {atWar && <span className={styles.warActiveBadge}>⚔ At War</span>}

      {/* banner */}
      <img src={bannerSrc} alt="" className={styles.factionBanner} />

      {/* body */}
      <div className={styles.factionCardBody}>
        <img
          src={avatarSrc}
          alt={faction.name}
          className={styles.factionAvatar}
          style={{ borderColor: getFactionColor(faction) }}
        />
        <span className={styles.factionCardName}>{faction.name}</span>
        <span className={styles.factionCardTag}>[{faction.tag}]</span>
        {faction.slogan && (
          <span className={styles.factionCardSlogan}>{faction.slogan}</span>
        )}
        <div className={styles.factionCardMeta}>
          {faction.points != null && (
            <span className={styles.factionCardMetaItem}>⭐ {faction.points} pts</span>
          )}
          {faction.level != null && (
            <span className={styles.factionCardMetaItem}>Lv {faction.level}</span>
          )}
        </div>
      </div>
    </Link>
  );
};

// ---- FactionTableView (list) ------------------------------------------------

interface FactionTableViewProps {
  factions: Faction[];
  activeWarFactionIds: Set<number>;
  onRowClick: (tag: string) => void;
}

const FactionTableView: React.FC<FactionTableViewProps> = ({
  factions,
  activeWarFactionIds,
  onRowClick,
}) => {
  return (
    <table className={styles.factionTable}>
      <thead>
        <tr>
          <th>Faction</th>
          <th>Tag</th>
          <th>Type</th>
          <th>Points</th>
          <th>Level</th>
          <th>Status</th>
        </tr>
      </thead>
      <tbody>
        {factions.map((f) => {
          const avatarSrc =
            f.profileURL ||
            new InitialsAPI().getAvatarURL({ name: f.tag, length: 4, fontSize: 0.35 });
          return (
            <tr key={f.id} onClick={() => onRowClick(f.tag)}>
              <td>
                <img src={avatarSrc} alt="" className={styles.tableAvatar} />
                <span className={styles.tableFactionName}>{f.name}</span>
              </td>
              <td>
                <span className={styles.tableTag}>[{f.tag}]</span>
              </td>
              <td>
                <span
                  className={styles.tableType}
                  style={{ background: typeBgColor(f.type) }}
                >
                  {typeLabel(f.type)}
                </span>
              </td>
              <td>{f.points ?? "–"}</td>
              <td>{f.level ?? "–"}</td>
              <td>
                {activeWarFactionIds.has(f.id) ? (
                  <span style={{ color: "#e74c3c", fontWeight: 600 }}>⚔ At War</span>
                ) : (
                  <span style={{ color: "#4caf50" }}>Peace</span>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

export default FactionsDashboardNew;
